import { api } from '@/lib/api';

// STUDIO PASS (blueprint 04: monthly credits plus a priority queue). The
// offer and the member's pass from /api/web/pass (routes/studio-pass.js),
// which reads shared/config/studio-pass.js: off until the owner sets
// STUDIO_PASS=1, price and credits are Render settings.

export interface PassOffer {
  enabled: boolean;
  price_cents: number; // centavos MXN, what Stripe charges each month
  usd_cents: number; // display anchor only
  credits: number; // granted each paid month
  priority: boolean;
  interval: 'month';
  currency: string;
}

export interface PassMember {
  status: string;
  active: boolean;
  renews_at: string | null;
  cancel_at_period_end: boolean;
  credits: number;
  price_cents: number;
}

function headers(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = api.getToken();
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

async function call<T>(path: string, init?: RequestInit): Promise<{ ok: boolean; status: number; body: T & { error?: string } }> {
  const res = await fetch(`/api/web/pass${path}`, { ...init, headers: { ...headers(), ...(init?.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, body };
}

export async function loadPassOffer(): Promise<PassOffer | null> {
  try {
    const r = await call<{ pass: PassOffer }>('');
    return r.ok ? r.body.pass : null;
  } catch {
    return null;
  }
}

export async function loadPassMember(): Promise<PassMember | null> {
  if (!api.getToken()) return null;
  try {
    const r = await call<{ member: PassMember | null }>('/me');
    return r.ok ? r.body.member : null;
  } catch {
    return null;
  }
}

/** Stripe Checkout for the pass; throws the server's error code. */
export async function startPassCheckout(lang: string): Promise<string> {
  const r = await call<{ url?: string }>('/checkout', { method: 'POST', body: JSON.stringify({ lang }) });
  if (!r.ok || !r.body.url) throw new Error(r.body.error || 'checkout_failed');
  return r.body.url;
}

/** Stop (true) or keep (false) renewing at the end of the paid month. */
export async function setPassCancel(cancel: boolean): Promise<PassMember> {
  const r = await call<{ member: PassMember }>(cancel ? '/cancel' : '/resume', { method: 'POST', body: '{}' });
  if (!r.ok || !r.body.member) throw new Error(r.body.error || 'pass_update_failed');
  return r.body.member;
}

export const mxn = (cents: number) => `$${Math.round(cents / 100).toLocaleString('en-US')}`;
export const usd = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
/** MXN per credit, the number that compares the pass with the packs. */
export const perCredit = (o: Pick<PassOffer, 'price_cents' | 'credits'>) => (o.price_cents / 100 / o.credits).toFixed(2);
